import {
  Badge,
  Button,
  Divider,
  Group,
  Popover,
  Stack,
  Text,
} from "@mantine/core";
import {
  IconBuilding,
  IconClock,
  IconMapPin,
  IconPencil,
  IconTrash,
  IconUsers,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import { Link } from "react-router-dom";

import { getTagColor } from "../Tags/utils/getTagColor";
import { dateOf, timeOf } from "./utils/sessionTime";

function DetailRow({ icon: Icon, children }) {
  return (
    <Group gap={8} wrap="nowrap" align="flex-start">
      <Icon
        size={16}
        color="var(--mantine-color-dimmed)"
        style={{ flexShrink: 0, marginTop: 2 }}
      />
      <Text size="sm" component="div">
        {children}
      </Text>
    </Group>
  );
}

export default function SessionPopover({
  session,
  day,
  opened,
  onClose,
  onDelete,
  enableActions,
  tagColors,
  children,
}) {
  const tags = [...(session.targetedTags || []), ...(session.tags || [])];
  const speakers = session.speakers ?? [];
  const endsNextDay = dateOf(session.ending) > dateOf(session.starting);

  return (
    <Popover
      opened={opened}
      onChange={(isOpened) => {
        if (!isOpened) onClose();
      }}
      position="right"
      withArrow
      shadow="md"
      width={320}
      trapFocus
      returnFocus
    >
      <Popover.Target>{children}</Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          <Group gap="xs" wrap="nowrap" align="flex-start">
            <Text fw={600} style={{ flex: 1 }}>
              {session.title}
            </Text>
            {session.canceledAt !== null && (
              <Badge color="red" variant="light" size="sm">
                Canceled
              </Badge>
            )}
          </Group>

          <DetailRow icon={IconClock}>
            {dayjs(day).format("dddd, MMMM D")} · {timeOf(session.starting)} –{" "}
            {timeOf(session.ending)}
            {endsNextDay && " (+1 day)"}
          </DetailRow>

          {session.location?.name && (
            <DetailRow icon={IconMapPin}>{session.location.name}</DetailRow>
          )}

          {speakers.length > 0 && (
            <DetailRow icon={IconUsers}>
              {speakers.map((speaker, index) => (
                <div key={speaker?.id ?? index}>
                  {speaker?.occupation
                    ? `${speaker?.firstName} ${speaker?.lastName}, ${speaker?.occupation}`
                    : `${speaker?.firstName} ${speaker?.lastName}`}
                </div>
              ))}
            </DetailRow>
          )}

          {session.sponsor?.id && (
            <DetailRow icon={IconBuilding}>{session.sponsor.name}</DetailRow>
          )}

          {tags.length > 0 && (
            <Group gap={4}>
              {tags.map((tag) => (
                <Badge
                  key={tag.id}
                  color={getTagColor(tagColors, tag.subtype)?.color}
                  variant="light"
                  size="sm"
                >
                  {tag.title}
                </Badge>
              ))}
            </Group>
          )}

          <Divider />

          <Group justify="flex-end" gap="xs">
            <Button
              size="xs"
              variant="light"
              leftSection={<IconPencil size={14} />}
              component={Link}
              to={`${session.id}`}
            >
              Edit
            </Button>
            <Button
              size="xs"
              variant="light"
              color="red"
              leftSection={<IconTrash size={14} />}
              onClick={onDelete}
              disabled={!enableActions}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
